import { useWallet, truncateAddress } from '@/hooks/useWallet';

interface ConnectWalletPromptProps {
  /** The action user needs to complete after connecting */
  actionDescription?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Component that displays a prompt to connect wallet with a button
 * Used on pages that require wallet connection to interact
 */
export function ConnectWalletPrompt({
  actionDescription = 'interact with this feature',
  className = '',
}: ConnectWalletPromptProps): JSX.Element {
  const { connect, isConnecting } = useWallet();

  const handleConnect = (): void => {
    void connect();
  };

  return (
    <div
      className={`bg-blue-50 border border-blue-200 rounded-lg p-6 text-center ${className}`}
    >
      <div className="flex justify-center mb-4">
        <div className="bg-blue-100 rounded-full p-3">
          <svg
            className="w-8 h-8 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
        </div>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Connect Your Wallet
      </h3>
      <p className="text-gray-600 mb-4">
        Please connect your wallet to {actionDescription}.
      </p>
      <button
        onClick={handleConnect}
        disabled={isConnecting}
        className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isConnecting ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Connecting...
          </>
        ) : (
          <>
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            Connect Wallet
          </>
        )}
      </button>
      <p className="text-sm text-gray-500 mt-4">
        Supports Keplr, Cosmostation, and Leap wallets
      </p>
    </div>
  );
}

interface WalletStatusBadgeProps {
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the full address or truncated */
  showFullAddress?: boolean;
}

/**
 * A smaller badge-style component showing wallet connection status
 * Can be used inline on pages
 */
export function WalletStatusBadge({
  className = '',
  showFullAddress = false,
}: WalletStatusBadgeProps): JSX.Element | null {
  const { isConnected, address, username, walletIcon, openView } = useWallet();

  if (!isConnected) {
    return null;
  }

  const displayAddress = showFullAddress ? address : truncateAddress(address);
  const displayName = username ?? displayAddress;

  return (
    <button
      onClick={openView}
      className={`inline-flex items-center px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium hover:bg-green-200 transition-colors ${className}`}
    >
      {walletIcon !== undefined && walletIcon !== '' && (
        <img
          src={walletIcon}
          alt="wallet"
          className="w-4 h-4 mr-2 rounded"
        />
      )}
      <span className="mr-1">Connected:</span>
      <span className="font-mono">{displayName}</span>
    </button>
  );
}
